import React, { useEffect, useState } from 'react';
import compose from 'lodash.flowright';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import Alert from '../../../../components/global/Alert';
import Button from '../../../../components/global/Button';
import Spinner from '../../../../components/global/Spinner';
import InputGroup from '../../../../components/global/InputGroup';
import { getAsset } from '../../../../util/assets';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  accountUpdate as accountUpdateMutation,
  passwordCreate as passwordCreateMutation,
} from '../../../../graphql/mutations/account';
import ErrorView from '../../../../components/global/ErrorView';
import { getInvitiationDetailsFromSearchQuery } from '../../../pro/views/InvitationResolver/helpers';
import SignUpLayout from '../../../../components/global/SignUpLayout';

const propTypes = {
  authenticated: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  passwordCreate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    has_password: PropTypes.bool,
  }),
};

const PasswordSetup = ({
  authenticated,
  history,
  location,
  passwordCreate,
  accountUpdate,
  user,
}) => {
  const {
    next,
    first_name: firstName,
    last_name: lastName,
    // because we might've come here from a previous "next" url,
    // args might go haywire. We'll append them accordingly to the next url
    ...nextArgs
  } = qs.parse(location?.search);

  const nextWithArgs =
    Object.keys(nextArgs).length === 0
      ? next
      : next?.indexOf('?') !== -1
        ? `${next}&${qs.stringify(nextArgs, { arrayFormat: 'bracket' })}`
        : `${next}?${qs.stringify(nextArgs, { arrayFormat: 'bracket' })}`;
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const contributorInvitationDetails =
    getInvitiationDetailsFromSearchQuery(location);

  useEffect(() => {
    setIsFormValid(password !== '' && password === passwordConfirmation);
  }, [password, passwordConfirmation]);

  // Should only get to this page with a next param, so bail if unset.
  if (!next) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'Sorry, something has gone wrong as the URL is missing a required parameter.',
          },
        }}
      />
    );
  }

  // Likewise a user must be authed before getting to this page.
  if (!authenticated) {
    history.replace('/shutterbug/signin');
  }

  if (!user) {
    // Show a spinner until we get the user, since we can't pass the next check with or without it.
    return <Spinner />;
  }

  // Lastly, a user must not already have a password set.
  if (user.has_password) {
    // Silently forward them, no need to show anything since their set password intent is ultimately fulfilled.
    history.replace(nextWithArgs);
  }

  const attemptSetPassword = async () => {
    setLoading(true);
    try {
      await passwordCreate({ accountId: user.id, password });
      // If there's first name,  last name, update the account with these
      if (firstName || lastName) {
        await accountUpdate({
          id: user.id,
          firstName,
          lastName,
        });
      }

      if (contributorInvitationDetails) {
        history.push(
          `/contributors/invres?${qs.stringify(contributorInvitationDetails)}`,
        );

        return;
      }

      history.replace(nextWithArgs);
    } catch (err) {
      setError(getErrorString(err));
      setLoading(false);
    }
  };

  return (
    <SignUpLayout.Wrapper>
      <SignUpLayout.Content>
        <SignUpLayout.Title title="Set a password" subtitle="Almost done!" />
        <div className="mb-4">
          Now pick a secure password to be able to access your account in a safe
          manner.
        </div>
        <form
          className="password-setup-form"
          onSubmit={event => {
            event.preventDefault();
            attemptSetPassword();
          }}
        >
          <InputGroup
            addon={
              <img alt="" src={getAsset('/shutterbug/icons/password.svg')} />
            }
            id="password"
            name="password"
            onChange={event => setPassword(event.target.value)}
            placeholder="Password"
            required
            type="password"
            value={password}
            wrapperClassName="mb-2"
          />
          <InputGroup
            addon={
              <img alt="" src={getAsset('/shutterbug/icons/password.svg')} />
            }
            id="password-confirmation"
            name="password-confirmation"
            onChange={event => setPasswordConfirmation(event.target.value)}
            placeholder="Confirm password"
            required
            type="password"
            value={passwordConfirmation}
            wrapperClassName="mb-2"
          />
          {error && (
            <Alert type="danger" className="mt-2">
              <strong>Setting your password wasn&apos;t successful</strong>
              <br />
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            className="btn-block mt-4"
            disabled={loading || !isFormValid}
          >
            {loading ? <Spinner size="xs" light /> : 'Continue'}
          </Button>
        </form>
      </SignUpLayout.Content>
    </SignUpLayout.Wrapper>
  );
};

PasswordSetup.propTypes = propTypes;

export { PasswordSetup as PasswordSetupComponent };

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const withPasswordCheck = graphql(
  gql`
    query meQuery {
      me {
        id
        has_password
      }
    }
  `,
  {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data: { me } }) => ({
      user: me,
    }),
    skip: ownProps => !ownProps.authenticated,
  },
);

const withPasswordCreateMutation = graphql(passwordCreateMutation, {
  props: ({ mutate }) => ({
    passwordCreate: ({ accountId, password }) =>
      mutate({
        variables: {
          input: { account_id: accountId, password },
        },
      }),
  }),
});

const withAccountUpdateMutation = graphql(accountUpdateMutation, {
  props: ({ mutate }) => ({
    accountUpdate: ({ id, firstName, lastName }) =>
      mutate({
        variables: {
          input: {
            id,
            name: `${firstName} ${lastName}`.trim(),
          },
        },
      }),
  }),
});

export default compose(
  connect(mapStateToProps),
  withPasswordCheck,
  withPasswordCreateMutation,
  withAccountUpdateMutation,
)(PasswordSetup);
